const getWindow = () => (typeof window !== 'undefined' ? window : null);

export interface InputSkeletonState {
	query: string;
	placeholder: string;
	isFocused: boolean;
	selectionStart: number;
	selectionEnd: number;
	selectionDirection: 'forward' | 'backward' | 'none';
	listeners: SSREventListener[];
}

export type SSREventListener<
	TNode = Node,
	TEventName = EventName,
	TEventListener = EventListener,
> = [TNode, TEventName, TEventListener];

export type EventName = keyof HTMLElementEventMap;

export type EventListener = <T extends EventName>(
	this: HTMLElement,
	ev: HTMLElementEventMap[T],
) => any;
declare global {
	interface Window {
		inputSkeletonState?: InputSkeletonState;
	}
}

interface skeletonSelection {
	selectionStart: number;
	selectionEnd: number;
	selectionDirection: 'forward' | 'backward' | 'none';
}

const getInputSkeletonQuery = (): string => {
	return getWindow()?.inputSkeletonState?.query ?? '';
};

const setInputSkeletonQuery = (newQuery: string): void => {
	const { inputSkeletonState } = getWindow() ?? {};

	if (inputSkeletonState) {
		inputSkeletonState.query = newQuery;
	}
};

const getInputSkeletonFocus = (): boolean => {
	return getWindow()?.inputSkeletonState?.isFocused || false;
};

const setInputSkeletonIsFocus = (newIsFocused: boolean): void => {
	const { inputSkeletonState } = getWindow() ?? {};

	if (inputSkeletonState) {
		inputSkeletonState.isFocused = newIsFocused;
	}
};

const getInputSkeletonSelection = (): skeletonSelection => {
	const { inputSkeletonState } = getWindow() ?? {};

	return {
		selectionStart: inputSkeletonState?.selectionStart ?? 0,
		selectionEnd: inputSkeletonState?.selectionEnd ?? 0,
		selectionDirection: inputSkeletonState?.selectionDirection ?? 'none',
	};
};

const setInputSkeletonSelection = (
	newSelectionStart: number,
	newSelectionEnd: number,
	newSelectionDirection: 'forward' | 'backward' | 'none',
): void => {
	const { inputSkeletonState } = getWindow() ?? {};

	if (inputSkeletonState) {
		inputSkeletonState.selectionStart = newSelectionStart;
		inputSkeletonState.selectionEnd = newSelectionEnd;
		inputSkeletonState.selectionDirection = newSelectionDirection;
	}
};

const resetInputSkeletonState = (): void => {
	const safeWindow = getWindow();

	if (safeWindow) {
		safeWindow.inputSkeletonState = {
			query: '',
			placeholder: '',
			isFocused: false,
			selectionStart: 0,
			selectionEnd: 0,
			selectionDirection: 'none',
			listeners: [],
		};
	}
};

const removeInputSkeletonEventListeners = (): void => {
	const { inputSkeletonState } = getWindow() ?? {};

	if (inputSkeletonState) {
		inputSkeletonState.listeners.forEach(([target, event, handler]) => {
			target.removeEventListener(event, handler);
		});
		inputSkeletonState.listeners = [];
	}
};

const removeInputSkeletonState = (): void => {
	removeInputSkeletonEventListeners();
	delete getWindow()?.inputSkeletonState;
};

export {
	getInputSkeletonQuery,
	setInputSkeletonQuery,
	getInputSkeletonFocus,
	setInputSkeletonIsFocus,
	getInputSkeletonSelection,
	setInputSkeletonSelection,
	resetInputSkeletonState,
	removeInputSkeletonEventListeners,
	removeInputSkeletonState,
};
